import { TIME_OPTIONS } from "@/pages/subscriptions/components/wizard/steps/customizeSubscription/constants";

export const FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

export const TIME_SELECT_OPTIONS = TIME_OPTIONS.map(time => ({ label: time, value: time }));

export const DAY_OF_WEEK_OPTIONS = [
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
  { label: "Sunday", value: "0" },
];

const getOrdinalSuffix = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

export const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
  value: String(i + 1),
}));

/**
 * Converts frequency and time selections to a cron expression
 */
export const buildCronExpression = (
  frequency: string,
  time: string,
  dayOfWeek: string = "1",
  dayOfMonth: string = "1"
): string => {
  // Parse 12-hour time format to 24-hour
  const timeParts = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!timeParts) {
    return "0 0 * * *"; // Default: midnight daily
  }

  let hours = parseInt(timeParts[1], 10);
  const minutes = parseInt(timeParts[2], 10);
  const period = timeParts[3].toUpperCase();

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  // Build cron based on frequency
  switch (frequency) {
    case "daily":
      return `${minutes} ${hours} * * *`;
    case "weekly":
      return `${minutes} ${hours} * * ${dayOfWeek}`;
    case "monthly":
      return `${minutes} ${hours} ${dayOfMonth} * *`;
    default:
      return `${minutes} ${hours} * * *`;
  }
};

export interface ParsedCronExpression {
  frequency: string;
  time: string;
  dayOfWeek: string;
  dayOfMonth: string;
}

/**
 * Parses a cron expression to extract frequency, time, and day values
 */
export const parseCronExpression = (cron: string): ParsedCronExpression => {
  const parts = cron.split(" ");
  if (parts.length !== 5) {
    return { frequency: "daily", time: "12:00 AM", dayOfWeek: "1", dayOfMonth: "1" };
  }

  const [minute, hour, dayOfMonthPart, , dayOfWeekPart] = parts;

  // Determine frequency and extract day values
  let frequency = "daily";
  let dayOfWeek = "1"; // Default to Monday
  let dayOfMonth = "1"; // Default to 1st

  if (dayOfWeekPart !== "*") {
    frequency = "weekly";
    dayOfWeek = dayOfWeekPart;
  } else if (dayOfMonthPart !== "*") {
    frequency = "monthly";
    dayOfMonth = dayOfMonthPart;
  }

  // Parse time (handle wildcards)
  let hours = parseInt(hour, 10);
  const minutes = parseInt(minute, 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return { frequency, time: "12:00 AM", dayOfWeek, dayOfMonth };
  }

  // Convert to 12-hour format
  const period = hours >= 12 ? "PM" : "AM";
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;

  const time = `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;

  return { frequency, time, dayOfWeek, dayOfMonth };
};
